import { uiText } from '@/i18n/interface';

import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { toast } from "@/hooks/use-toast";
import { useAuth } from '@/contexts/AuthContext';

interface AdminStats {
  totalStudents: string;
  activeSessions: string;
  pendingResponses: string;
  advancedStudents: string;
}

export const useAdminStats = () => {
  const { profile } = useAuth();
  const [stats, setStats] = useState<AdminStats>({
    totalStudents: "0",
    activeSessions: "0",
    pendingResponses: "0",
    advancedStudents: "0"
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  const fetchStats = async () => {
    try {
      setIsLoadingStats(true);

      // Get current user's role and dojo info
      const currentDojoId = profile?.dojo_id;
      const isSuperUser = profile?.role === 'super_user';

      console.log('fetchStats - Super user:', isSuperUser, 'Dojo ID:', currentDojoId);

      // Regular admins MUST have a dojo_id
      if (!isSuperUser && !currentDojoId) {
        console.log('No dojo_id available for regular admin user');
        setStats({
          totalStudents: "0",
          activeSessions: "0",
          pendingResponses: "0",
          advancedStudents: "0"
        });
        return;
      }

      // Build query for students
      let studentsQuery = supabase
        .from('profiles')
        .select('id')
        .eq('role', 'student');

      // Only filter by dojo if we're not a super_user, OR if super_user is in impersonation mode
      if (!isSuperUser || (isSuperUser && currentDojoId)) {
        studentsQuery = studentsQuery.eq('dojo_id', currentDojoId);
        console.log('Filtering students by dojo_id:', currentDojoId);
      } else {
        console.log('Super user - fetching ALL students globally');
      }

      const { data: studentsData, error: studentsError } = await studentsQuery;

      if (studentsError) throw studentsError;

      // Fetch active sessions (sessions scheduled for today or in the future)
      const today = new Date().toISOString().split('T')[0];
      let sessionsQuery = supabase
        .from('training_sessions')
        .select('id')
        .gte('date', today);

      if (!isSuperUser || (isSuperUser && currentDojoId)) {
        sessionsQuery = sessionsQuery.eq('dojo_id', currentDojoId);
        console.log('Filtering sessions by dojo_id:', currentDojoId);
      } else {
        console.log('Super user - fetching ALL sessions globally');
      }

      const { data: sessionsData, error: sessionsError } = await sessionsQuery;

      if (sessionsError) throw sessionsError;

      // Fetch promotion sessions (upcoming promotions)
      let promotionQuery = supabase
        .from('promotion_sessions')
        .select('id')
        .gte('date', today);

      if (!isSuperUser || (isSuperUser && currentDojoId)) {
        promotionQuery = promotionQuery.eq('dojo_id', currentDojoId);
      }

      const { data: promotionData, error: promotionError } = await promotionQuery;

      if (promotionError) throw promotionError;

      // Fetch advanced students (brown belts and above - order >= 6 for brown belt)
      let beltsQuery = supabase
        .from('belt_levels')
        .select('id')
        .gte('order_sequence', 6);

      if (!isSuperUser || (isSuperUser && currentDojoId)) {
        beltsQuery = beltsQuery.eq('dojo_id', currentDojoId);
      }

      const { data: beltsData, error: beltsError } = await beltsQuery;

      if (beltsError) throw beltsError;

      const beltIds = beltsData?.map(belt => belt.id) || [];

      let advancedStudentsCount = 0;
      if (beltIds.length > 0) {
        let advancedQuery = supabase
          .from('profiles')
          .select('id')
          .eq('role', 'student')
          .in('belt_level_id', beltIds);

        if (!isSuperUser || (isSuperUser && currentDojoId)) {
          advancedQuery = advancedQuery.eq('dojo_id', currentDojoId);
        }

        const { data: advancedData, error: advancedError } = await advancedQuery;

        if (advancedError) throw advancedError;
        advancedStudentsCount = advancedData?.length || 0;
      }

      setStats({
        totalStudents: (studentsData?.length || 0).toString(),
        activeSessions: (sessionsData?.length || 0).toString(),
        pendingResponses: (promotionData?.length || 0).toString(),
        advancedStudents: advancedStudentsCount.toString()
      });

    } catch (error) {
      console.error('Error fetching stats:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to load dashboard statistics."),
        variant: "destructive"
      });
    } finally {
      setIsLoadingStats(false);
    }
  };

  useEffect(() => {
    if (profile) {
      fetchStats();
    }
  }, [profile?.dojo_id, profile?.role]);

  return {
    stats,
    isLoadingStats,
    fetchStats
  };
};
